import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { GmailMessage } from '../types/gmail';

export function useRefreshMessages() {
  const [refreshing, setRefreshing] = useState(false);

  const refreshMessages = async (email: string) => {
    setRefreshing(true);
    try {
      // Get all Gmail accounts
      const { data: accounts, error: accountsError } = await supabase
        .from('nrm_gmail_credentials')
        .select('*');

      if (accountsError) throw accountsError;

      // Fetch messages from each account
      for (const account of accounts || []) {
        const { data: messages, error: messagesError } = await supabase.functions.invoke(
          'fetch-gmail-messages',
          {
            body: { 
              email,
              accessToken: account.access_token,
              accountEmail: account.email
            }
          }
        );

        if (messagesError) {
          console.error(`Error fetching messages from ${account.email}:`, messagesError);
          continue;
        }

        // Transform messages for database storage
        const messagesToInsert = messages.map((msg: GmailMessage) => ({
          gmail_id: msg.id,
          thread_id: msg.threadId,
          account_email: msg.accountEmail,
          subject: msg.subject,
          snippet: msg.snippet,
          from_email: msg.from,
          to_emails: msg.to,
          date: new Date(msg.date)
        }));

        // Store messages in database
        if (messagesToInsert.length > 0) {
          const { error: upsertError } = await supabase
            .from('nrm_email_messages')
            .upsert(messagesToInsert, {
              onConflict: 'gmail_id,account_email'
            });

          if (upsertError) {
            console.error('Error storing messages:', upsertError);
          }
        }
      }
    } catch (err) {
      console.error('Error refreshing messages:', err);
      throw err;
    } finally {
      setRefreshing(false);
    }
  };

  return { refreshMessages, refreshing };
}